/* Picco collection cards. No global product-card listeners and no framework. */
(() => {
  'use strict';
  if (customElements.get('picco-collection-card')) return;

  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const shopRoot = () => window.Shopify?.routes?.root || '/';
  const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));
  const wishlistKey = 'picco:collection-favourites:v1';
  let memoryWishlist = [];
  let wishlistStorageAvailable = true;

  const COLOUR_PALETTE = [
    ['off white', '#eee9df'], ['off-white', '#eee9df'], ['washed black', '#3f3f3d'],
    ['washed grey', '#8a8a86'], ['washed gray', '#8a8a86'], ['heather grey', '#aaa9a5'],
    ['heather gray', '#aaa9a5'], ['light grey', '#d3d3cf'], ['light gray', '#d3d3cf'],
    ['dark grey', '#5b5b58'], ['dark gray', '#5b5b58'], ['charcoal grey', '#555652'],
    ['charcoal gray', '#555652'], ['light wash', '#a9bfd2'], ['mid wash', '#7391aa'],
    ['dark wash', '#3d586f'], ['baby blue', '#b3cfe2'], ['sky blue', '#9fc5df'],
    ['light blue', '#a8c5d9'], ['dark blue', '#384f69'], ['navy blue', '#27384f'],
    ['forest green', '#435a45'], ['olive green', '#6d6c4c'], ['sage green', '#9ba38c'],
    ['army green', '#5d624b'], ['bottle green', '#344f43'], ['mint green', '#adc8b6'],
    ['chocolate brown', '#51372d'], ['dark brown', '#5c4436'], ['light brown', '#a17d62'],
    ['dusty pink', '#c69b9e'], ['hot pink', '#cf6e91'], ['pale pink', '#dfbdc2'],
    ['stone grey', '#97958c'], ['slate grey', '#777d7b'], ['ash grey', '#a5a5a0'],
    ['royal blue', '#48658b'], ['cream', '#eee5d2'], ['ecru', '#e6decf'], ['ivory', '#f1eee4'],
    ['oatmeal', '#c8bda8'], ['mushroom', '#a09386'], ['chocolate', '#51372d'],
    ['burgundy', '#643842'], ['maroon', '#653b3b'], ['charcoal', '#555652'], ['mustard', '#b9953f'],
    ['turquoise', '#4f9292'], ['purple', '#756282'], ['lilac', '#b9a7c4'], ['orange', '#c87946'],
    ['yellow', '#d0b653'], ['silver', '#b7b9b7'], ['gold', '#b79b5b'], ['white', '#f5f5f1'],
    ['black', '#202020'], ['grey', '#999995'], ['gray', '#999995'], ['stone', '#b8af9e'],
    ['sand', '#c7b99d'], ['taupe', '#9b8b7c'], ['mocha', '#806553'], ['camel', '#b48257'],
    ['tan', '#b39370'], ['brown', '#6d4d3d'], ['navy', '#27384f'], ['blue', '#54728f'],
    ['green', '#55705a'], ['olive', '#6d6c4c'], ['sage', '#9ba38c'], ['khaki', '#8c836b'],
    ['red', '#a34540'], ['pink', '#d3a0aa'], ['teal', '#4b787b'], ['beige', '#c7bda9']
  ];

  function normaliseText(value = '') {
    return String(value).toLowerCase().replace(/&amp;/g, '&').replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function titleCase(value = '') {
    return String(value).replace(/\b\w/g, character => character.toUpperCase());
  }

  function paletteColour(label = '') {
    const normalised = normaliseText(label);
    if (!normalised) return null;
    const found = COLOUR_PALETTE.find(([name]) => normalised === normaliseText(name) || normalised.endsWith(` ${normaliseText(name)}`));
    return found ? { label: titleCase(found[0].replace('-', ' ')), hex: found[1] } : null;
  }

  function titleColour(title = '') {
    const normalised = normaliseText(title);
    if (!normalised) return null;
    const matches = COLOUR_PALETTE
      .map(([name, hex]) => ({ name: normaliseText(name), label: titleCase(name.replace('-', ' ')), hex }))
      .sort((a, b) => b.name.length - a.name.length);
    return matches.find(item => normalised === item.name || normalised.endsWith(` ${item.name}`)) || null;
  }

  function descriptionColour(product = {}) {
    const source = String(product.description || product.body_html || '');
    const match = source.match(/colou?r\s*:\s*(?:<[^>]+>\s*)*([^<\n]+)/i);
    if (!match) return null;
    const raw = match[1].replace(/&nbsp;/g, ' ').trim();
    return paletteColour(raw) || { label: titleCase(normaliseText(raw)), hex: '#dcdcd7' };
  }

  function productColour(product = {}) {
    const explicit = product.colour || {};
    if (explicit.label) {
      const mapped = paletteColour(explicit.label);
      return { label: explicit.label, hex: explicit.hex || mapped?.hex || '#dcdcd7' };
    }
    return titleColour(product.title) || descriptionColour(product);
  }

  function familyKeyFor(product = {}) {
    if (product.familyKey) return normaliseText(product.familyKey).replace(/\s+/g, '-');
    const words = normaliseText(product.title).split(' ').filter(Boolean);
    if (words.length < 2) return words.join('-');
    let take = 2;
    if (words[0] === 'picco' && (words[1] === 'studios' || words[1] === 'studio') && words.length > 2) take = 3;
    return words.slice(0, take).join('-');
  }

  function stripTitleColour(title = '', colour = null) {
    if (!colour?.label) return String(title).split(' - ')[0].trim();
    const raw = String(title).trim();
    const label = normaliseText(colour.label);
    const words = raw.replace(/\s+-\s+/g, ' ').trim().split(/\s+/);
    const labelWords = label.split(' ');
    const tail = normaliseText(words.slice(-labelWords.length).join(' '));
    if (tail === label) return words.slice(0, -labelWords.length).join(' ').replace(/[\s\-–—]+$/, '').trim();
    return raw.split(' - ')[0].trim();
  }

  function swatchNeedsBorder(hex = '') {
    const value = String(hex).replace('#', '');
    if (!/^[0-9a-f]{6}$/i.test(value)) return false;
    const rgb = [0, 2, 4].map(index => parseInt(value.slice(index, index + 2), 16));
    return rgb.every(channel => channel > 220);
  }

  function readWishlist() {
    if (!wishlistStorageAvailable) return memoryWishlist;
    try {
      const saved = JSON.parse(localStorage.getItem(wishlistKey) || '[]');
      if (Array.isArray(saved)) memoryWishlist = saved.filter(item => item && typeof item === 'object' && item.id != null);
    } catch (_) { wishlistStorageAvailable = false; }
    return memoryWishlist;
  }

  function writeWishlist(items) {
    memoryWishlist = items;
    if (wishlistStorageAvailable) {
      try { localStorage.setItem(wishlistKey, JSON.stringify(items)); }
      catch (_) { wishlistStorageAvailable = false; }
    }
    document.querySelectorAll('picco-collection-card').forEach(card => card.syncSaved());
    document.dispatchEvent(new CustomEvent('picco:wishlist:change', { detail: { items } }));
  }

  window.addEventListener('storage', event => {
    if (event.key === wishlistKey || event.key === null) {
      readWishlist();
      document.querySelectorAll('picco-collection-card').forEach(card => card.syncSaved());
    }
  });

  function getCartDrawer() {
    return document.querySelector('cart-drawer') || document.getElementById('CartDrawer') || document.querySelector('[data-cart-drawer]');
  }

  function cartSections(drawer) {
    let ids = [];
    try {
      if (typeof drawer?.getSectionsToRender === 'function') {
        ids = drawer.getSectionsToRender().map(section => section.section || section.id);
      } else if (drawer) {
        const wrapper = drawer.closest('[id^="shopify-section-"]');
        const id = drawer.dataset.sectionId || wrapper?.id.replace('shopify-section-', '');
        if (id) ids.push(id);
      }
    } catch (_) {}
    return [...new Set(ids.filter(id => typeof id === 'string' && id.length))].slice(0, 5);
  }

  function drawerIsOpen(drawer) {
    if (!drawer) return false;
    return drawer.hasAttribute('open') || drawer.classList.contains('active') ||
      drawer.classList.contains('is-open') || (drawer.hasAttribute('aria-hidden') && drawer.getAttribute('aria-hidden') === 'false');
  }

  async function openCartDrawer(drawer, trigger) {
    if (!drawer) {
      location.assign(shopRoot() + 'cart');
      return;
    }
    if (drawerIsOpen(drawer)) return;
    if (typeof drawer.show === 'function') {
      await drawer.show();
      return;
    }
    if (typeof drawer.open === 'function') {
      await drawer.open(trigger);
      return;
    }
    // Use the theme's own opener, rather than just removing `hidden` and
    // bypassing its focus trap, overlay and body-scroll management.
    const opener = [...document.querySelectorAll('[aria-controls]')].find(node =>
      node.getAttribute('aria-controls') === drawer.id &&
      !drawer.contains(node) && !node.closest('dialog') && !node.hidden &&
      !node.matches('overlay-element, .overlay, [disabled]') && node.getClientRects().length
    );
    if (opener) {
      opener.click();
      return;
    }
    location.assign(shopRoot() + 'cart');
  }

  async function updateAndOpenCart(result, variantId, trigger) {
    let drawer = getCartDrawer();
    if (typeof drawer?.setActiveElement === 'function') drawer.setActiveElement(trigger);
    const ids = cartSections(drawer);
    const hasRenderedSections = ids.length && ids.every(id => typeof result.sections?.[id] === 'string');

    if (typeof drawer?.renderContents === 'function' && hasRenderedSections) {
      // Dawn-style drawers render server HTML and manage their own opening.
      await drawer.renderContents({ ...result, id: result.id || variantId });
      return;
    }

    // Preserve the cart:refresh hook already used by the supplied section.
    // It is theme-specific, not a built-in Shopify API event.
    let cart = null;
    try {
      const response = await fetch(shopRoot() + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (response.ok) cart = await response.json();
    } catch (_) { /* The add succeeded; never retry it because refresh failed. */ }
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { open: true, cart, sections: result.sections || {}, id: variantId, source: 'picco-collection' }
    }));
    await nextFrame();
    drawer = getCartDrawer();
    await openCartDrawer(drawer, trigger);
  }

  class PiccoCollectionCard extends HTMLElement {
    connectedCallback() {
      // When parsed by a script in <head>, connectedCallback can run before
      // the element's children exist. A microtask also covers AJAX insertion.
      queueMicrotask(() => { if (this.isConnected) this.init(); });
    }

    init() {
      // Track123 owns these galleries, including gestures and quick add.
      if (this.dataset.piccoTrackCard === 'true') return;
      if (this.events) return;
      this.track = this.querySelector('[data-pc-track]');
      if (!this.track) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.slides = [...this.querySelectorAll('[data-pc-slide]')];
      this.previous = this.querySelector('[data-pc-prev]');
      this.next = this.querySelector('[data-pc-next]');
      this.status = this.querySelector('[data-pc-gallery-status]');
      this.activeIndex = 0;
      this.trackWidth = 0;
      this.skipClickUntil = 0;
      this.manualImageOverride = false;
      this.isViewportVisible = true;
      this.programmaticImageUntil = 0;
      this.userImageInteractionUntil = 0;
      const pendingImagePreference = this.dataset.emePendingImagePreference;
      this.preferredImageIndex = pendingImagePreference
        ? (pendingImagePreference === 'second' ? 1 : 0)
        : (localStorage.getItem('eme-image-view') === 'second' ? 1 : 0);
      delete this.dataset.emePendingImagePreference;
      this.data = null;
      try { this.data = JSON.parse(this.querySelector('[data-pc-product]')?.textContent || 'null'); } catch (_) {}
      if (this.data) {
        this.data.familyKey = familyKeyFor(this.data);
        this.data.inferredColour = productColour(this.data);
      }
      this.previous?.addEventListener('click', () => this.goTo(this.activeIndex - 1, { manual: true }), { signal });
      this.next?.addEventListener('click', () => this.goTo(this.activeIndex + 1, { manual: true }), { signal });
      this.track.addEventListener('scroll', () => {
        const now = performance.now();
        if (now < this.userImageInteractionUntil && now > this.programmaticImageUntil) this.manualImageOverride = true;
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.syncGallery();
        });
        clearTimeout(this.statusTimer);
        this.statusTimer = setTimeout(() => {
          if (this.status && this.slides.length > 1) this.status.textContent = `Image ${this.activeIndex + 1} of ${this.slides.length}`;
        }, 150);
      }, { passive: true, signal });
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? this.slides.length - 1 :
          this.activeIndex + (event.key === 'ArrowRight' ? 1 : -1);
        this.goTo(index, { manual: true });
      }, { signal });
      this.track.addEventListener('dragstart', event => event.preventDefault(), { signal });
      this.track.addEventListener('pointerdown', event => this.startDrag(event), { signal });
      this.track.addEventListener('pointermove', event => this.moveDrag(event), { signal });
      this.track.addEventListener('pointerup', event => this.endDrag(event), { signal });
      this.track.addEventListener('pointercancel', event => this.endDrag(event), { signal });
      this.track.addEventListener('pointerleave', event => {
        if (this.drag && !this.drag.dragged) this.endDrag(event);
      }, { signal });
      this.track.addEventListener('click', event => {
        if (performance.now() < this.skipClickUntil) {
          event.preventDefault();
          event.stopPropagation();
        }
      }, { capture: true, signal });
      this.querySelector('[data-pc-quick-add]')?.addEventListener('click', event => {
        const collection = this.closest('picco-collection');
        if (this.data && collection?.quickAdd) collection.quickAdd.open(this.data, event.currentTarget);
        else if (this.data?.url) location.assign(this.data.url);
      }, { signal });
      this.querySelector('[data-pc-save]')?.addEventListener('click', () => this.toggleSaved(), { signal });
      this.syncSaved();
      this.closest('picco-collection')?.scheduleColourSync?.();
      this.resizeObserver = new ResizeObserver(() => {
        const width = this.track.clientWidth;
        if (width > 0 && width !== this.trackWidth) {
          const savedIndex = this.activeIndex;
          this.trackWidth = width;
          this.track.scrollTo({ left: savedIndex * width, behavior: 'instant' });
          this.querySelectorAll('.pc-card__image').forEach(image => { image.sizes = `${Math.ceil(width)}px`; });
          this.syncGallery();
        }
      });
      this.resizeObserver.observe(this.track);
      this.setupImageLoaders();

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          const entry = entries[0];
          if (!entry) return;
          this.isViewportVisible = entry.isIntersecting;
          if (!entry.isIntersecting) {
            this.manualImageOverride = false;
            this.applyImagePreference(this.preferredImageIndex === 1, { force: true, behavior: 'auto' });
          }
        }, { threshold: 0 });
        this.visibilityObserver.observe(this);
      }

      this.syncGallery();
      this.applyImagePreference(this.preferredImageIndex === 1, { force: true, behavior: 'auto' });
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      clearTimeout(this.statusTimer);
    }

    setupImageLoaders() {
      const signal = this.events?.signal;
      this.querySelectorAll('[data-pc-image-shell]').forEach(slide => {
        const image = slide.querySelector('img');
        if (!image) { slide.classList.add('is-image-loaded'); return; }
        const complete = () => slide.classList.add('is-image-loaded');
        if (image.complete) complete();
        else {
          image.addEventListener('load', complete, { once: true, signal });
          image.addEventListener('error', complete, { once: true, signal });
        }
      });
    }

    applyImagePreference(useSecond, { force = false, behavior = 'auto' } = {}) {
      const target = useSecond && this.slides.length > 1 ? 1 : 0;
      this.preferredImageIndex = target;
      if (force) this.manualImageOverride = false;
      if (!force && this.manualImageOverride && this.isViewportVisible) return;

      const width = this.track?.clientWidth || this.slides[0]?.clientWidth || 0;
      if (!width) {
        requestAnimationFrame(() => {
          if (this.isConnected) this.applyImagePreference(useSecond, { force, behavior });
        });
        return;
      }

      this.userImageInteractionUntil = 0;
      this.programmaticImageUntil = performance.now() + 650;
      this.activeIndex = target;
      try {
        this.track.scrollTo({ left: target * width, top: 0, behavior: reduceMotion() ? 'auto' : behavior });
      } catch (_) {
        this.track.scrollLeft = target * width;
      }
      requestAnimationFrame(() => this.syncGallery());
    }

    goTo(index, { manual = true, behavior = 'smooth' } = {}) {
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      if (manual) {
        this.manualImageOverride = true;
        this.userImageInteractionUntil = performance.now() + 1200;
      } else {
        this.userImageInteractionUntil = 0;
        this.programmaticImageUntil = performance.now() + 650;
      }
      this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduceMotion() ? 'auto' : behavior });
    }

    syncGallery() {
      const width = this.track.clientWidth;
      if (!width) return;
      this.activeIndex = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
      for (const [button, disabled] of [[this.previous, this.activeIndex === 0], [this.next, this.activeIndex >= this.slides.length - 1]]) {
        if (button) { button.disabled = disabled; button.setAttribute('aria-disabled', String(disabled)); }
      }
      this.slides.forEach((slide, index) => { slide.tabIndex = index === this.activeIndex ? 0 : -1; });
    }

    startDrag(event) {
      this.userImageInteractionUntil = performance.now() + 1600;
      if (event.pointerType !== 'mouse' || event.button !== 0 || this.slides.length < 2) return;
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.track.scrollLeft, dragged: false };
    }

    moveDrag(event) {
      this.userImageInteractionUntil = performance.now() + 1600;
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.dragged && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
        drag.dragged = true;
        this.track.setPointerCapture(event.pointerId);
        this.track.style.scrollSnapType = 'none';
        this.track.classList.add('is-dragging');
      }
      if (drag.dragged) {
        event.preventDefault();
        this.track.scrollLeft = drag.left - dx;
      }
    }

    endDrag(event) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null;
      if (drag.dragged) {
        this.skipClickUntil = performance.now() + 300;
        const index = Math.round(this.track.scrollLeft / this.track.clientWidth);
        this.track.style.removeProperty('scroll-snap-type');
        this.track.classList.remove('is-dragging');
        if (this.track.hasPointerCapture(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
        this.goTo(index, { manual: true });
      }
    }

    syncSaved() {
      const button = this.querySelector('[data-pc-save]');
      if (!button || !this.data) return;
      const saved = readWishlist().some(item => String(item.id) === String(this.data.id));
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${this.data.title} ${saved ? 'from' : 'to'} favourites`);
    }

    renderColourFamily(family = []) {
      const root = this.querySelector('[data-pc-colours]');
      if (!root || !this.data) return;
      const colour = productColour(this.data);
      const nativeCount = Number(this.data.colour?.nativeCount) || 1;
      const familyColours = [...new Map(
        family
          .map(item => [normaliseText(productColour(item)?.label || ''), item])
          .filter(([key]) => key)
      ).values()];
      const additional = Math.max(nativeCount - 1, familyColours.length - 1, 0);

      if (!colour && additional <= 0) {
        if (!root.children.length) root.hidden = true;
        return;
      }

      root.replaceChildren();
      if (colour) {
        const swatch = document.createElement('span');
        swatch.className = 'pc-card__swatch';
        swatch.setAttribute('aria-hidden', 'true');
        swatch.style.backgroundColor = colour.hex || '#dcdcd7';
        swatch.classList.toggle('is-light', swatchNeedsBorder(colour.hex));
        root.append(swatch);
      }
      if (additional > 0) {
        const count = document.createElement('span');
        count.className = 'pc-card__colour-count';
        count.setAttribute('aria-hidden', 'true');
        count.textContent = `+${additional}`;
        root.append(count);
      }
      const labels = familyColours.map(item => productColour(item)?.label).filter(Boolean);
      root.setAttribute('aria-label', labels.length > 1 ? `${colour?.label || 'Product colour'}; ${additional} additional colours` : (colour?.label || 'Product colour'));
      root.hidden = false;
      if (this.data.trimTitle && familyColours.length > 1) {
        const title = this.querySelector('[data-pc-card-title]');
        if (title) title.textContent = stripTitleColour(this.data.cardTitle || this.data.title, colour);
      }
    }

    toggleSaved() {
      if (!this.data) return;
      const items = readWishlist();
      const saved = items.some(item => String(item.id) === String(this.data.id));
      const { id, title, url, image, price } = this.data;
      writeWishlist(saved ? items.filter(item => String(item.id) !== String(id)) : [...items, { id, title, url, image, price }]);
      if (this.status) this.status.textContent = saved ? 'Removed from favourites' : 'Saved to favourites on this device';
    }
  }

  class QuickAdd {
    constructor(collection, signal) {
      this.collection = collection;
      this.shell = collection.querySelector('[data-pc-quick-shell]');
      if (!this.shell) return;

      this.panel = this.shell.querySelector('[data-pc-quick-panel]');
      this.error = this.shell.querySelector('[data-pc-error]');
      this.variants = this.shell.querySelector('[data-pc-variants]');
      this.addButton = this.shell.querySelector('[data-pc-add]');
      this.addText = this.shell.querySelector('[data-pc-add-text]');
      this.inventory = this.shell.querySelector('[data-pc-inventory]');
      this.inventoryText = this.shell.querySelector('[data-pc-inventory-text]');
      this.swatches = this.shell.querySelector('[data-pc-swatches]');
      this.colourRow = this.shell.querySelector('[data-pc-colour-row]');
      this.activeColour = this.shell.querySelector('[data-pc-active-colour]');
      this.gallery = this.shell.querySelector('[data-pc-gallery]');
      this.galleryShell = this.shell.querySelector('[data-pc-gallery-shell]');
      this.saveButton = this.shell.querySelector('[data-pc-quick-save]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.shell.querySelectorAll('[data-pc-close]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          this.close();
        }, { signal });
      });

      this.addButton?.addEventListener('click', () => {
        if (this.selectedVariant) this.add(this.selectedVariant, this.addButton);
      }, { signal });
      this.saveButton?.addEventListener('click', () => this.toggleSaved(), { signal });

      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.isOpen()) this.close();
      }, { signal });
    }

    isOpen() {
      return !!this.shell && !this.shell.hidden && this.shell.classList.contains('is-open');
    }

    open(product, trigger) {
      if (!this.shell || this.busy) return;
      if (!product.variants?.length) {
        location.assign(product.url);
        return;
      }

      clearTimeout(this.closeTimer);
      this.product = product;
      this.trigger = trigger;
      this.selectedVariant = product.variants.find(variant => variant.available) || product.variants[0] || null;
      this.error.hidden = true;
      this.error.textContent = '';

      const colour = productColour(product);
      const family = this.collection.getColourFamily(product);
      const displayTitle = stripTitleColour(product.cardTitle || product.title, colour);
      this.shell.querySelector('[data-pc-title]').textContent = displayTitle;
      this.shell.querySelector('[data-pc-price]').textContent = product.price;
      const compare = this.shell.querySelector('[data-pc-compare-price]');
      compare.hidden = !product.comparePrice;
      compare.textContent = product.comparePrice || '';
      const badge = this.shell.querySelector('[data-pc-badge]');
      badge.hidden = !product.badge;
      badge.textContent = product.badge || '';
      this.shell.querySelector('[data-pc-product-link]').href = product.url;

      this.renderGallery(product);
      this.renderColours(product, family);
      this.renderVariants(product);
      this.syncSaved();
      this.selectVariant(this.selectedVariant, false);

      this.lockScroll();
      trigger.setAttribute('aria-expanded', 'true');
      this.shell.hidden = false;
      this.shell.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => {
        this.shell.classList.add('is-open');
        const first = this.variants.querySelector('.pc-variant.is-selected:not(:disabled)') || this.addButton;
        first?.focus({ preventScroll: true });
      });
    }

    renderGallery(product) {
      if (!this.gallery) return;
      const images = Array.isArray(product.gallery) && product.gallery.length ? product.gallery : (product.image ? [product.image] : []);
      this.gallery.replaceChildren();
      images.forEach((src, index) => {
        const slide = document.createElement('div');
        slide.className = 'pc-quick-add__gallery-slide';
        const image = document.createElement('img');
        image.src = src;
        image.alt = `${product.title} - image ${index + 1}`;
        image.loading = index === 0 ? 'eager' : 'lazy';
        image.decoding = 'async';
        image.draggable = false;
        const markLoaded = () => slide.classList.add('is-image-loaded');
        if (image.complete) markLoaded();
        else {
          image.addEventListener('load', markLoaded, { once: true });
          image.addEventListener('error', markLoaded, { once: true });
        }
        slide.append(image);
        this.gallery.append(slide);
      });
      if (this.galleryShell) this.galleryShell.hidden = images.length === 0;
      this.gallery.scrollLeft = 0;
      this.setupGalleryDrag();
    }

    setupGalleryDrag() {
      const rail = this.gallery;
      if (!rail || rail.dataset.pcDragReady === 'true') return;
      rail.dataset.pcDragReady = 'true';
      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;

      rail.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse') return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = rail.scrollLeft;
        rail.classList.add('is-dragging');
        try { rail.setPointerCapture(pointerId); } catch (_) {}
      });

      rail.addEventListener('pointermove', event => {
        if (pointerId !== event.pointerId) return;
        rail.scrollLeft = startScrollLeft - (event.clientX - startX);
      });

      const end = event => {
        if (pointerId !== event.pointerId) return;
        rail.classList.remove('is-dragging');
        try { rail.releasePointerCapture(pointerId); } catch (_) {}
        pointerId = null;
      };
      rail.addEventListener('pointerup', end);
      rail.addEventListener('pointercancel', end);
    }

    renderColours(product, family) {
      this.swatches.replaceChildren();
      const current = productColour(product);
      const unique = [...new Map(
        family.map(item => [normaliseText(productColour(item)?.label || ''), item]).filter(([key]) => key)
      ).values()];
      this.colourRow.hidden = unique.length < 2 || !current;
      if (this.colourRow.hidden) return;
      this.activeColour.textContent = current.label;

      unique.forEach(item => {
        const colour = productColour(item);
        if (!colour) return;
        const isCurrent = String(item.id) === String(product.id);
        const node = document.createElement(isCurrent ? 'span' : 'a');
        node.className = `pc-colour-swatch-group${isCurrent ? ' is-active' : ''}`;
        node.title = colour.label;
        node.setAttribute('aria-label', colour.label);
        if (!isCurrent) node.href = item.url || `${shopRoot()}products/${item.handle}`;
        const swatch = document.createElement('span');
        swatch.className = 'pc-colour-swatch';
        swatch.style.backgroundColor = colour.hex || '#dcdcd7';
        swatch.classList.toggle('is-light', swatchNeedsBorder(colour.hex));
        node.append(swatch);
        this.swatches.append(node);
      });
    }

    renderVariants(product) {
      this.variants.replaceChildren();
      const fragment = document.createDocumentFragment();
      product.variants.forEach((variant, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pc-variant';
        button.dataset.variantId = variant.id;
        button.dataset.available = variant.available ? 'true' : 'false';
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', 'false');
        button.disabled = !variant.available;
        if (!variant.available) button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-label', `${variant.title}${variant.available ? '' : ', sold out'}`);

        const qty = Number(variant.inventoryQuantity || 0);
        let stockLevel = '';
        if (variant.available && qty > 0 && qty <= 10) stockLevel = 'red';
        else if (variant.available && qty > 0 && qty <= 20) stockLevel = 'orange';
        if (stockLevel) button.dataset.stockLevel = stockLevel;

        const top = document.createElement('span');
        top.className = 'pc-variant__topline';
        const label = document.createElement('span');
        label.className = 'pc-variant__name';
        label.textContent = variant.title;
        top.append(label);
        if (stockLevel) {
          const dot = document.createElement('span');
          dot.className = 'pc-variant__dot';
          dot.setAttribute('aria-hidden', 'true');
          top.append(dot);
        }

        const mark = document.createElement('span');
        mark.className = 'pc-variant__mark';
        mark.setAttribute('aria-hidden', 'true');
        if (!variant.available) {
          mark.innerHTML = '<svg viewBox="0 0 14 10" width="14" height="10" fill="none" aria-hidden="true"><path d="m1.866 3.487 5.133 2.104 5.134-2.104" stroke="currentColor" stroke-miterlimit="10"/><path d="M1.866 2.143h10.267v5.714H1.866z" stroke="currentColor" stroke-miterlimit="10.433"/></svg>';
        }
        button.append(top, mark);
        if (!variant.available) button.classList.add('is-sold-out');
        button.addEventListener('click', () => {
          if (!variant.available) return;
          this.selectVariant(variant, true);
        });
        if (index === 0) button.tabIndex = 0;
        fragment.append(button);
      });
      this.variants.append(fragment);
    }

    selectVariant(variant, focus = true) {
      if (!variant) return;
      this.selectedVariant = variant;
      this.variants.querySelectorAll('.pc-variant').forEach(button => {
        const selected = String(button.dataset.variantId) === String(variant.id);
        button.classList.toggle('is-selected', selected);
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
        if (selected && focus) button.focus({ preventScroll: true });
      });
      this.addButton.disabled = !variant.available;
      this.syncInventory(variant);
    }

    syncInventory(variant) {
      if (!this.inventory || !this.inventoryText) return;
      const quantity = Number(variant?.inventoryQuantity || 0);
      let text = '';
      let level = '';
      if (!variant?.available) {
        text = 'Sold out';
        level = 'red';
      } else if (quantity > 0) {
        text = `${quantity} item${quantity === 1 ? '' : 's'} left`;
        if (quantity <= 10) level = 'red';
        else if (quantity <= 20) level = 'orange';
      } else {
        text = 'In stock';
      }
      this.inventory.hidden = false;
      this.inventoryText.textContent = text;
      if (level) this.inventory.dataset.stockLevel = level;
      else delete this.inventory.dataset.stockLevel;
    }

    syncSaved() {
      if (!this.saveButton || !this.product) return;
      const saved = readWishlist().some(item => String(item.id) === String(this.product.id));
      this.saveButton.setAttribute('aria-pressed', String(saved));
      this.saveButton.setAttribute('aria-label', `${saved ? 'Remove from' : 'Add to'} favourites`);
      this.saveButton.classList.toggle('is-active', saved);
    }

    toggleSaved() {
      if (!this.product) return;
      const items = readWishlist();
      const saved = items.some(item => String(item.id) === String(this.product.id));
      const { id, title, url, image, price } = this.product;
      writeWishlist(saved ? items.filter(item => String(item.id) !== String(id)) : [...items, { id, title, url, image, price }]);
      this.syncSaved();
    }

    lockScroll() {
      if (this.scrollState) return;
      const root = document.documentElement;
      const body = document.body;
      this.scrollState = { rootOverflow: root.style.overflow, rootGutter: root.style.scrollbarGutter, bodyOverflow: body.style.overflow };
      if (window.innerWidth > root.clientWidth && getComputedStyle(root).scrollbarGutter === 'auto') root.style.scrollbarGutter = 'stable';
      root.style.overflow = 'hidden';
      body.style.overflow = 'hidden';
    }

    cleanup() {
      if (this.scrollState) {
        document.documentElement.style.overflow = this.scrollState.rootOverflow;
        document.documentElement.style.scrollbarGutter = this.scrollState.rootGutter;
        document.body.style.overflow = this.scrollState.bodyOverflow;
        this.scrollState = null;
      }
      this.trigger?.setAttribute('aria-expanded', 'false');
      this.product = null;
      this.selectedVariant = null;
    }

    close(immediate = false) {
      if (!this.shell || this.shell.hidden) return;
      clearTimeout(this.closeTimer);
      this.shell.classList.remove('is-open');
      this.shell.setAttribute('aria-hidden', 'true');
      const trigger = this.trigger;
      const finish = () => {
        this.shell.hidden = true;
        this.cleanup();
        trigger?.focus?.({ preventScroll: true });
      };
      if (immediate || this.reducedMotion.matches) finish();
      else this.closeTimer = window.setTimeout(finish, 500);
    }

    destroy() { this.close(true); }

    async add(variant, button) {
      if (this.busy || !variant?.available) return;
      this.busy = true;
      this.error.hidden = true;
      this.shell.classList.add('is-adding');
      button.disabled = true;
      let added = false;
      try {
        const sections = cartSections(getCartDrawer());
        const payload = { items: [{ id: variant.id, quantity: Math.max(1, Number(variant.quantity) || 1) }] };
        if (sections.length) {
          payload.sections = sections;
          payload.sections_url = location.pathname + location.search;
        }
        const feedback = window.PiccoCartFeedback?.begin({ shell: this.shell, product: this.product, variant, trigger: this.trigger, close: () => this.close() });
        this.cartFeedback = feedback;
        const response = await fetch(shopRoot() + 'cart/add.js', {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload)
        });
        let result;
        try { result = await response.json(); }
        catch (_) { throw new Error('We could not confirm the cart update. Check your cart before trying again.'); }
        if (!response.ok || result.status >= 400) {
          const message = typeof result.description === 'string' ? result.description : result.message;
          throw new Error(message || 'This option could not be added. Please try another size.');
        }
        added = true;
        if (this.cartFeedback) {
          await window.PiccoCartFeedback.complete(this.cartFeedback, result);
          return;
        }
        const trigger = this.trigger;
        this.close(true);
        if (trigger?.isConnected) trigger.focus({ preventScroll: true });
        await nextFrame();
        await updateAndOpenCart(result, variant.id, trigger);
      } catch (error) {
        if (this.cartFeedback) {
          await window.PiccoCartFeedback.fail(this.cartFeedback, error, added);
        } else if (added) {
          console.warn('[Picco collection] Item added, but cart UI could not open.', error);
          location.assign(shopRoot() + 'cart');
        } else if (this.collection.isConnected) {
          this.error.textContent = error instanceof TypeError ?
            'We could not confirm the cart update. Check your cart before trying again.' :
            (error.message || 'Unable to add this size. Please try again.');
          this.error.hidden = false;
          if (!this.isOpen()) {
            this.lockScroll();
            this.shell.hidden = false;
            this.shell.setAttribute('aria-hidden', 'false');
            requestAnimationFrame(() => this.shell.classList.add('is-open'));
            this.trigger?.setAttribute('aria-expanded', 'true');
          }
          this.error.focus({ preventScroll: true });
        }
      } finally {
        this.cartFeedback = null;
        this.busy = false;
        this.shell.classList.remove('is-adding');
        if (this.selectedVariant) this.addButton.disabled = !this.selectedVariant.available;
      }
    }
  }

  class PiccoCollection extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected) this.init(); });
    }

    init() {
      if (this.events) return;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.mobileQuery = matchMedia('(max-width: 1023px)');
      this.defaults = { desktop: Number(this.dataset.columnsDesktop) || 4, mobile: Number(this.dataset.columnsMobile) || 2 };
      this.preferences = { ...this.defaults };
      this.infiniteLoadedUrls = new Set();
      this.colourCatalog = [];
      this.colourSyncTimer = null;
      // New keys deliberately avoid inheriting the previous forced 3-column
      // layout. Editor previews honour settings rather than stored preferences.
      if (!window.Shopify?.designMode) {
        for (const screen of ['desktop', 'mobile']) {
          try {
            const value = Number(localStorage.getItem(`picco:collection-grid:v1:${screen}`));
            if (value >= 1 && value <= (screen === 'mobile' ? 2 : 5)) this.preferences[screen] = value;
          } catch (_) {}
        }
      }
      this.quickAdd = new QuickAdd(this, signal);
      this.gridObserver = new MutationObserver(mutations => {
        if (!mutations.some(item => item.attributeName === 'class')) return;
        const desktop = [...this.grid.classList].find(value => /^card-grid--[1-5]$/.test(value));
        const mobile = [...this.grid.classList].find(value => /^mobile:card-grid--[12]$/.test(value));
        const desktopValue = desktop ? Number(desktop.slice(-1)) : null;
        const mobileValue = mobile ? Number(mobile.slice(-1)) : null;
        const mode = this.mobileQuery.matches ? 'mobile' : 'desktop';
        const desktopChanged = desktopValue && desktopValue !== this.appliedDesktop;
        const mobileChanged = mobileValue && mobileValue !== this.appliedMobile;
        if (desktopChanged || mobileChanged) {
          // The original toolbar may change the desktop class on mobile.
          this.setColumns(mode === 'mobile' && mobileChanged ? mobileValue : desktopValue);
        }
      });
      this.bindGrid();
      this.bindInfiniteScroll();
      this.scheduleColourSync();
      if (this.dataset.showColours === 'true') this.loadColourCatalog();
      this.contentObserver = new MutationObserver(() => {
        if (this.grid !== this.querySelector('.pc-grid')) this.bindGrid();
        if (
          this.pagination !== this.querySelector('[data-pc-pagination]') ||
          this.infiniteSentinel !== this.querySelector('[data-pc-infinite-sentinel]')
        ) {
          this.bindInfiniteScroll();
        }
      });
      this.contentObserver.observe(this, { childList: true, subtree: true });
      this.addEventListener('click', event => {
        const button = event.target.closest('button[data-layout-mode], button[data-grid], .layout-buttons button');
        if (!button || !this.contains(button)) return;
        const raw = button.dataset.grid || button.dataset.layoutMode || button.value || '';
        const match = String(raw).match(/^(?:grid[-_]?|columns[-_]?)?([1-5])$/);
        if (match) {
          // Run after the theme's own click handler without cancelling it.
          queueMicrotask(() => this.setColumns(Number(match[1])));
        }
      }, { signal });
      this.mobileQuery.addEventListener('change', () => this.applyGrid(), { signal });
    }


    scheduleColourSync() {
      clearTimeout(this.colourSyncTimer);
      this.colourSyncTimer = setTimeout(() => this.syncColourFamilies(), 0);
    }

    currentProductData() {
      return [...this.querySelectorAll('picco-collection-card')]
        .map(card => card.data)
        .filter(Boolean);
    }

    allColourProducts() {
      const merged = new Map();
      (Array.isArray(this.colourCatalog) ? this.colourCatalog : []).forEach(item => merged.set(String(item.id || item.handle), item));
      this.currentProductData().forEach(item => merged.set(String(item.id || item.handle), item));
      return [...merged.values()];
    }

    getColourFamily(product) {
      if (!product) return [];
      const key = familyKeyFor(product);
      if (!key) return [product];
      const candidates = this.allColourProducts().filter(item => familyKeyFor(item) === key && productColour(item));
      const uniqueColours = new Set(candidates.map(item => normaliseText(productColour(item)?.label || '')).filter(Boolean));
      if (uniqueColours.size < 2) return [product];
      candidates.sort((a, b) => String(a.id) === String(product.id) ? -1 : String(b.id) === String(product.id) ? 1 : 0);
      return candidates;
    }

    syncColourFamilies() {
      if (this.dataset.showColours !== 'true') return;
      this.querySelectorAll('picco-collection-card').forEach(card => {
        if (!card.data) return;
        card.renderColourFamily(this.getColourFamily(card.data));
      });
    }

    async loadColourCatalog() {
      const collectionUrl = this.dataset.collectionUrl;
      if (!collectionUrl || this.colourCatalogLoading || this.colourCatalogLoaded) return;
      this.colourCatalogLoading = true;
      try {
        const url = new URL(`${collectionUrl.replace(/\/$/, '')}/products.json`, location.origin);
        url.searchParams.set('limit', '250');
        const response = await fetch(url.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Colour catalogue request failed (${response.status})`);
        const payload = await response.json();
        this.colourCatalog = (payload.products || []).map(item => {
          const title = item.title || '';
          const image = item.images?.[0]?.src || item.image?.src || null;
          const entry = {
            id: item.id,
            title,
            handle: item.handle,
            url: `${shopRoot()}products/${item.handle}`,
            image,
            description: item.body_html || '',
            familyKey: familyKeyFor({ title })
          };
          entry.inferredColour = productColour(entry);
          return entry;
        });
        this.colourCatalogLoaded = true;
        this.syncColourFamilies();
      } catch (error) {
        // Card grouping still works from products already present in the grid.
        console.warn('[Picco collection] Could not preload colour families.', error);
      } finally {
        this.colourCatalogLoading = false;
      }
    }

    bindGrid() {
      this.gridObserver.disconnect();
      this.grid = this.querySelector('.pc-grid');
      this.applyGrid();
    }

    setColumns(value) {
      const mode = this.mobileQuery.matches ? 'mobile' : 'desktop';
      if (!Number.isFinite(value) || value < 1) return;
      value = Math.min(mode === 'mobile' ? 2 : 5, Math.round(value));
      this.preferences[mode] = value;
      if (!window.Shopify?.designMode) {
        try { localStorage.setItem(`picco:collection-grid:v1:${mode}`, String(value)); } catch (_) {}
      }
      this.applyGrid();
    }

    applyGrid() {
      const desktop = this.preferences.desktop;
      const mobile = this.preferences.mobile;
      this.style.setProperty('--pc-columns-desktop', desktop);
      this.style.setProperty('--pc-columns-mobile', mobile);
      if (!this.grid) return;
      this.gridObserver.disconnect();
      [...this.grid.classList].forEach(token => {
        if (/^(mobile:)?card-grid--\d+$/.test(token)) this.grid.classList.remove(token);
      });
      this.grid.classList.add(`card-grid--${desktop}`, `mobile:card-grid--${mobile}`);
      this.appliedDesktop = desktop;
      this.appliedMobile = mobile;
      this.gridObserver.observe(this.grid, { attributes: true, attributeFilter: ['class'] });
      const current = this.mobileQuery.matches ? mobile : desktop;
      this.querySelectorAll('button[data-layout-mode], button[data-grid], .layout-buttons button').forEach(button => {
        const raw = button.dataset.grid || button.dataset.layoutMode || button.value || '';
        const match = String(raw).match(/^(?:grid[-_]?|columns[-_]?)?([1-5])$/);
        if (!match) return;
        const selected = Number(match[1]) === current;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
      });
    }

    bindInfiniteScroll() {
      const previousPagination = this.pagination;

      this.infiniteObserver?.disconnect();
      this.infiniteObserver = null;
      if (this.infiniteScrollFallback) {
        window.removeEventListener('scroll', this.infiniteScrollFallback);
        window.removeEventListener('resize', this.infiniteScrollFallback);
        this.infiniteScrollFallback = null;
      }

      this.pagination = this.querySelector('[data-pc-pagination]');
      this.infiniteSentinel = this.querySelector('[data-pc-infinite-sentinel]');
      this.infiniteStatus = this.querySelector('[data-pc-infinite-status]');
      this.paginationMode = this.dataset.paginationMode || this.pagination?.dataset.paginationMode || '';

      // A replaced ProductGridContainer means filtering/sorting has started a new
      // result set, so old page URLs must not block the new infinite-scroll run.
      if (previousPagination && previousPagination !== this.pagination) {
        this.infiniteRequestController?.abort();
        this.infiniteRequestController = null;
        this.infiniteLoadedUrls = new Set();
      this.colourCatalog = [];
      this.colourSyncTimer = null;
        this.infiniteLoading = false;
      }

      if (this.paginationMode !== 'infinite' || !this.pagination || !this.infiniteSentinel) return;

      this.infiniteNextUrl = this.pagination.dataset.nextUrl || '';
      if (!this.infiniteNextUrl) {
        this.finishInfiniteScroll();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.infiniteObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadMoreProducts();
        }, { root: null, rootMargin: '1200px 0px', threshold: 0.01 });
        this.infiniteObserver.observe(this.infiniteSentinel);
      } else {
        this.infiniteScrollFallback = () => {
          if (!this.infiniteSentinel || this.infiniteLoading || !this.infiniteNextUrl) return;
          const rect = this.infiniteSentinel.getBoundingClientRect();
          if (rect.top <= window.innerHeight + 1200) this.loadMoreProducts();
        };
        window.addEventListener('scroll', this.infiniteScrollFallback, { passive: true });
        window.addEventListener('resize', this.infiniteScrollFallback, { passive: true });
        this.infiniteScrollFallback();
      }
    }

    setInfiniteStatus(message = '', retry = false, loading = false) {
      if (!this.infiniteStatus) return;
      this.infiniteStatus.replaceChildren();
      this.infiniteStatus.classList.toggle('is-loading', loading);
      if (loading) {
        const spinner = document.createElement('span');
        spinner.className = 'pc-loading-spinner';
        spinner.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.className = 'pc-visually-hidden';
        label.textContent = message || 'Loading more products';
        this.infiniteStatus.append(spinner, label);
        return;
      }
      if (!message) return;
      if (!retry) {
        this.infiniteStatus.textContent = message;
        return;
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pc-infinite-retry';
      button.textContent = message;
      button.addEventListener('click', () => this.loadMoreProducts(), { once: true });
      this.infiniteStatus.append(button);
    }

    sourceCollectionFromDocument(doc) {
      const id = this.dataset.sectionId;
      return [...doc.querySelectorAll('picco-collection')].find(section => section.dataset.sectionId === id) ||
        doc.querySelector('picco-collection');
    }

    async loadMoreProducts() {
      if (this.infiniteLoading || !this.infiniteNextUrl || !this.grid || !this.isConnected) return;

      let requestUrl;
      try { requestUrl = new URL(this.infiniteNextUrl, window.location.href).href; }
      catch (_) { requestUrl = this.infiniteNextUrl; }

      if (!requestUrl || this.infiniteLoadedUrls.has(requestUrl)) {
        this.finishInfiniteScroll();
        return;
      }

      this.infiniteLoading = true;
      this.setInfiniteStatus('Loading more products', false, true);
      this.infiniteRequestController?.abort();
      const controller = new AbortController();
      this.infiniteRequestController = controller;

      try {
        const response = await fetch(requestUrl, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Unable to load products (${response.status})`);

        const html = await response.text();
        if (controller.signal.aborted || !this.isConnected) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const sourceCollection = this.sourceCollectionFromDocument(doc);
        const sourceGrid = sourceCollection?.querySelector('.pc-grid');
        if (!sourceGrid) throw new Error('The next product grid could not be found.');

        const cards = [...sourceGrid.children];
        if (!cards.length) {
          this.finishInfiniteScroll();
          return;
        }

        const fragment = document.createDocumentFragment();
        cards.forEach(card => fragment.append(document.importNode(card, true)));
        this.grid.append(fragment);
        this.infiniteLoadedUrls.add(requestUrl);
        this.scheduleColourSync();

        const nextPagination = sourceCollection.querySelector('[data-pc-pagination]');
        this.infiniteNextUrl = nextPagination?.dataset.nextUrl || '';
        if (this.pagination) this.pagination.dataset.nextUrl = this.infiniteNextUrl;

        // Let the toolbar and any theme integrations resync newly appended cards.
        document.dispatchEvent(new CustomEvent('picco:infinite-products-loaded', {
          detail: { collection: this, count: cards.length, nextUrl: this.infiniteNextUrl }
        }));
        document.dispatchEvent(new CustomEvent('eme:collection-products-loaded', {
          detail: { root: this.grid, count: cards.length }
        }));

        if (typeof window.emeApplyCollectionCardImageView === 'function') {
          requestAnimationFrame(() => window.emeApplyCollectionCardImageView(false, 'auto'));
        }

        if (!this.infiniteNextUrl) this.finishInfiniteScroll();
        else this.setInfiniteStatus('');
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('[Picco collection] Infinite scroll failed:', error);
        this.setInfiniteStatus('Load more products', true);
      } finally {
        if (this.infiniteRequestController === controller) this.infiniteRequestController = null;
        this.infiniteLoading = false;

        // If the new row is short and the sentinel is still already inside the
        // preload zone, trigger the next page without requiring a manual nudge.
        if (this.infiniteNextUrl && this.infiniteSentinel && this.infiniteStatus?.textContent === '') {
          requestAnimationFrame(() => {
            if (!this.infiniteSentinel || this.infiniteLoading) return;
            const rect = this.infiniteSentinel.getBoundingClientRect();
            if (rect.top <= window.innerHeight + 1200) this.loadMoreProducts();
          });
        }
      }
    }

    finishInfiniteScroll() {
      this.infiniteNextUrl = '';
      if (this.pagination) this.pagination.dataset.nextUrl = '';
      this.infiniteObserver?.disconnect();
      this.infiniteObserver = null;
      if (this.infiniteScrollFallback) {
        window.removeEventListener('scroll', this.infiniteScrollFallback);
        window.removeEventListener('resize', this.infiniteScrollFallback);
        this.infiniteScrollFallback = null;
      }
      this.setInfiniteStatus('');
      this.infiniteSentinel?.remove();
      this.infiniteSentinel = null;
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.contentObserver?.disconnect();
      this.gridObserver?.disconnect();
      this.infiniteObserver?.disconnect();
      this.infiniteRequestController?.abort();
      if (this.infiniteScrollFallback) {
        window.removeEventListener('scroll', this.infiniteScrollFallback);
        window.removeEventListener('resize', this.infiniteScrollFallback);
      }
      clearTimeout(this.colourSyncTimer);
      this.quickAdd?.destroy();
    }
  }

  customElements.define('picco-collection', PiccoCollection);
  customElements.define('picco-collection-card', PiccoCollectionCard);
})();
